import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { Text } from '@ui/lib/park/ui/text';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV3/types';
import BigNumber from 'bignumber.js';
import { ReactNode } from 'react';
import { HStack, HstackProps, Stack, styled } from 'styled-system/jsx';

import { formatLeverage } from '../TradeModule/utils/formatLeverage';

/**
 * Team review #28: Pair & Side is ONE component across position / open order /
 * decrease order / history tables — 22px round icon vertically centered on a
 * two-line stack (#25), pair name on line 1, a single combined side+leverage
 * pill ("Long 5.0x") on line 2.
 */

/**
 * Ostium-style quiet chip (owner decision 2026-07-10, supersedes #33's
 * two-chip split): ONE integrated "Long 5.0x" — dim tint bg, NO border,
 * sentence case, no tracking. The chrome (border + ALL-CAPS + two chips)
 * was what made it loud; integration reads calm when the chip is quiet.
 */
export const SidePill = ({
  isLong,
  leverage,
  children,
  ...rest
}: {
  isLong: boolean;
  /** numeric leverage; rendered inline as " 5.0x" (letter x, owner preference) */
  leverage?: BigNumber | number;
  children?: ReactNode;
} & Record<string, unknown>) => {
  const leverageNumber =
    leverage === undefined
      ? undefined
      : BigNumber.isBigNumber(leverage)
        ? leverage.toNumber()
        : leverage;
  // A perp leverage below 1x cannot exist — TP/SL lifecycle history
  // events compute tiny garbage values (0.04 → renders "0.0x").
  // Anything < 1 is "not applicable": show the side alone.
  const hasLeverage =
    leverageNumber !== undefined &&
    leverageNumber !== null &&
    Number.isFinite(leverageNumber) &&
    leverageNumber >= 1;

  return (
    <styled.span
      display="inline-flex"
      alignItems="center"
      px="6px"
      py="3px"
      borderRadius="2px"
      bg={isLong ? 'v2.long.dim' : 'v2.short.dim'}
      color={isLong ? 'v2.long.default' : 'v2.short.default'}
      fontFamily="Inter"
      fontSize="10.5px"
      fontWeight={500}
      lineHeight="1"
      whiteSpace="nowrap"
      style={{ fontVariantNumeric: 'tabular-nums' }}
      data-cy="PairSidePill"
      data-cy-value={leverageNumber ?? '--'}
      {...rest}
    >
      {children ?? (
        <>
          {isLong ? 'Long' : 'Short'}
          {hasLeverage ? ` ${formatLeverage(leverageNumber)}x` : ''}
        </>
      )}
    </styled.span>
  );
};

export const PairSideCell = ({
  image,
  name,
  pill,
  nameExtras,
  ...rootProps
}: {
  image?: string;
  name: ReactNode;
  /** usually a <SidePill /> */
  pill?: ReactNode;
  /** extra inline elements after the name (market-time dot, chart eye, …) */
  nameExtras?: ReactNode;
} & HstackProps) => (
  <HStack gap={2} alignItems="center" minW={0} {...rootProps}>
    {image && (
      <styled.img
        src={image}
        boxSize="22px"
        borderRadius="full"
        flexShrink={0}
      />
    )}
    <Stack gap="3px" alignItems="flex-start" minW={0}>
      <HStack gap="4px" minW={0}>
        <Text
          fontSize="12.5px"
          fontWeight="500"
          lineHeight="18px"
          color="v2.text.primary"
          whiteSpace="nowrap"
          data-cy="PairName"
        >
          {name}
        </Text>
        {nameExtras}
      </HStack>
      {pill}
    </Stack>
  </HStack>
);

/** SidePill fed by a live position (leverage from current collateral price) */
export const PositionSidePill = ({ position }: { position: Position }) => {
  const collateralMarketPrice = usePayTokenPriceLatest(
    position.collateralToken,
  );
  const leverage = collateralMarketPrice
    ? position.getLeverage({
        collateralTokenMarketPrice: collateralMarketPrice,
      })
    : undefined;

  return <SidePill isLong={position.isLong} leverage={leverage} />;
};
